import { t } from '../i18n';

export type SmsFlowStatus = 'pending' | 'accepted' | 'delivered' | 'failed';

type FlowStepState = 'todo' | 'done' | 'current';

function flowStepStates(
  status: SmsFlowStatus,
  sawAccepted: boolean,
): { accepted: FlowStepState; pending: FlowStepState; terminal: FlowStepState } {
  switch (status) {
    case 'pending':
      return {
        accepted: 'current',
        pending: 'todo',
        terminal: 'todo',
      };
    case 'accepted':
      return {
        accepted: 'done',
        pending: 'current',
        terminal: 'todo',
      };
    case 'delivered':
      return {
        accepted: 'done',
        pending: 'done',
        terminal: 'current',
      };
    case 'failed':
      return {
        accepted: 'done',
        pending: sawAccepted ? 'done' : 'todo',
        terminal: 'current',
      };
  }
}

function createFlowStep(label: string, state: FlowStepState): HTMLSpanElement {
  const step = document.createElement('span');
  step.className = `sms-status-flow__step sms-status-flow__step--${state}`;
  step.textContent = label;
  return step;
}

function createFlowSeparator(): HTMLSpanElement {
  const sep = document.createElement('span');
  sep.className = 'sms-status-flow__sep';
  sep.setAttribute('aria-hidden', 'true');
  sep.textContent = '>';
  return sep;
}

export function buildSmsStatusFlow(
  status: SmsFlowStatus,
  sawAccepted: boolean,
  errorDescription: string | null,
): HTMLElement {
  const states = flowStepStates(status, sawAccepted);
  const flow = document.createElement('div');
  flow.className = 'sms-status-flow';

  const bar = document.createElement('div');
  bar.className = 'sms-status-flow__bar';
  bar.setAttribute('role', 'list');

  const terminalFailed = status === 'failed';
  const steps: Array<{ label: string; state: FlowStepState; kind?: 'delivered' | 'failed' }> = [
    { label: t('sms.flow.accepted'), state: states.accepted },
    { label: t('sms.flow.pending'), state: states.pending },
    {
      label: terminalFailed ? t('sms.flow.failed') : t('sms.flow.delivered'),
      state: states.terminal,
      kind: terminalFailed ? 'failed' : 'delivered',
    },
  ];

  steps.forEach((entry, index) => {
    if (index > 0) {
      bar.append(createFlowSeparator());
    }
    const step = createFlowStep(entry.label, entry.state);
    if (entry.state === 'current' && entry.kind === 'failed') {
      step.classList.add('sms-status-flow__step--failed');
    }
    if (entry.state === 'current' && entry.kind === 'delivered') {
      step.classList.add('sms-status-flow__step--delivered');
    }
    step.setAttribute('role', 'listitem');
    bar.append(step);
  });

  flow.append(bar);

  if (status === 'failed' && errorDescription?.trim()) {
    const reason = document.createElement('p');
    reason.className = 'sms-status-flow__reason';
    reason.textContent = errorDescription.trim();
    flow.append(reason);
  }

  return flow;
}
